import { useEffect, useState } from "react";
import { getConsent } from "./CookieConsent";

interface AdSlotProps {
  slot?: string;
  format?: string;
  className?: string;
  label?: string;
}

const ADSENSE_CLIENT = (import.meta.env.VITE_ADSENSE_CLIENT as string | undefined) ?? "ca-pub-9362953386244939";

/**
 * Ad slot that only renders after the visitor has accepted cookies.
 * If VITE_ADSENSE_CLIENT is configured, renders a Google AdSense unit.
 * Otherwise renders a neutral "Sponsored" placeholder so layout is preserved.
 */
export default function AdSlot({ slot, format = "auto", className = "", label = "Sponsored" }: AdSlotProps) {
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    setConsented(getConsent() === "accepted");
    const handler = (e: Event) => {
      const v = (e as CustomEvent).detail;
      setConsented(v === "accepted");
    };
    window.addEventListener("hvi-consent-change", handler);
    return () => window.removeEventListener("hvi-consent-change", handler);
  }, []);

  useEffect(() => {
    if (!consented || !ADSENSE_CLIENT || !slot) return;
    try {
      // @ts-expect-error - adsbygoogle is injected by the AdSense script
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* no-op */
    }
  }, [consented, slot]);

  if (!consented) {
    return (
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent("hvi-consent-open"))}
        className={`w-full border border-dashed border-border bg-card/40 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-hvi-primary hover:border-hvi-primary/60 p-4 text-center transition ${className}`}
      >
        Ad space — manage cookie preferences to support this site
      </button>
    );
  }

  if (!ADSENSE_CLIENT || !slot) {
    return (
      <div className={`border border-dashed border-border bg-card/40 text-[10px] uppercase tracking-wider text-muted-foreground p-4 text-center ${className}`}>
        {label}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <ins
        className="adsbygoogle block"
        style={{ display: "block" }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}
